import React from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Button,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Box,
    Text,
    Link,
    Flex,
} from '@chakra-ui/react';
import DOMPurify from 'dompurify';
import { CheckIcon } from '@chakra-ui/icons';

interface AccordionData {
    title: string;
    subtitle?: string;
    items: string[];
}

interface ModalSectionProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    subtitle: string;
    accordionData: AccordionData[];
    price: string;
    priceInfo?: string;
    link: string;
    linkName: string;
}

const ModalSection: React.FC<ModalSectionProps> = ({
    isOpen,
    onClose,
    title,
    subtitle,
    accordionData,
    price,
    priceInfo,
    link,
    linkName,
}) => {
    const sanitizedSubtitle = DOMPurify.sanitize(subtitle);

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
            <ModalOverlay />
            <ModalContent
                borderRadius="0"
                width={{ base: '95%', md: '1200px' }}
                maxWidth={{ base: '95%', md: '95%' }}
            >
                <ModalCloseButton />
                <ModalBody padding={{ base: '60px 10px 20px', md: '77px 30px 60px' }}>
                    <Box>
                        <Text
                            textAlign={{ base: 'center', md: 'left', lg: 'center' }}
                            fontSize={{
                                base: '30px',
                                lg: '34px',
                            }}
                            fontWeight="600"
                            lineHeight={{
                                base: '1.2',
                                lg: '1.4',
                            }}
                            padding={{
                                base: '0 15px',
                                md: '0',
                            }}
                            marginBottom={{
                                base: '10px',
                                lg: '15px',
                            }}
                        >
                            {title}
                        </Text>
                        <Text
                            padding={{
                                base: '0 15px',
                                md: '0',
                            }}
                            fontSize={{
                                base: '15px',
                                lg: '18px',
                            }}
                            textAlign={{ base: 'center', md: 'left', lg: 'center' }}
                            maxWidth="810px"
                            margin={{
                                base: '0 auto 15px',
                                lg: '0 auto 50px',
                            }}
                            dangerouslySetInnerHTML={{ __html: sanitizedSubtitle }}
                        />
                    </Box>
                    <Box
                        display={{
                            lg: 'flex',
                        }}
                    >
                        <Flex
                            justifyContent={{ base: 'center', md: 'flex-start' }}
                            order="2"
                            alignSelf="start"
                        >
                            <Box
                                padding="15px"
                                border="2px solid #171717"
                                marginBottom="20px"
                                width={{
                                    base: '100%',
                                    md: '300px',
                                    lg: '372px',
                                }}
                                marginLeft={{
                                    base: '0',
                                    lg: '70px',
                                }}
                            >
                                <Box flex="1" textAlign="left">
                                    <Text
                                        fontSize={{
                                            base: '30px',
                                            lg: '34px',
                                        }}
                                        lineHeight="1.4"
                                        fontWeight="600"
                                    >
                                        {price}
                                    </Text>
                                </Box>
                                {priceInfo && (
                                    <Text
                                        fontSize={{
                                            base: '11px',
                                            lg: '12px',
                                        }}
                                        color="#515151"
                                    >
                                        {priceInfo}
                                    </Text>
                                )}
                                <Link
                                    display={'inline-flex'}
                                    alignItems={'center'}
                                    fontWeight={'700'}
                                    transition={'.2s'}
                                    _hover={{
                                        color: '#DD1C4A',
                                        svg: {
                                            fill: '#DD1C4A',
                                        },
                                    }}
                                    href={link}
                                    marginTop="15px"
                                >
                                    <Text as={'span'} paddingRight={'10px'} fontSize={'14px'}>
                                        {linkName}
                                    </Text>
                                    <svg
                                        width="7"
                                        height="10"
                                        viewBox="0 0 9 14"
                                        fill="#171717"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path d="M0 13.6H2.70588L8.4 6.98565L2.70588 0.400024H0L5.69412 6.98565L0 13.6Z" />
                                    </svg>
                                </Link>
                            </Box>
                        </Flex>

                        <Accordion allowToggle allowMultiple flexGrow="1">
                            {accordionData.map((section, index, array) => (
                                <AccordionItem
                                    key={index}
                                    marginBottom={index === array.length - 1 ? '0' : '5px'}
                                    borderBottom="none"
                                >
                                    <AccordionButton
                                        padding={'5px 15px'}
                                        background={'#eeeeee'}
                                        color={'#171717'}
                                        fontSize={'18px'}
                                        fontWeight={'600'}
                                        justifyContent={'space-between'}
                                        alignItems={'center'}
                                        minHeight={'46px'}
                                    >
                                        <Text
                                            as="span"
                                            display="flex"
                                            flexDirection={{ base: 'column', xl: 'row' }}
                                            alignItems={{ base: 'flex-start', xl: 'baseline' }}
                                            lineHeight="normal"
                                        >
                                            {section.title}
                                            {section.subtitle && (
                                                <Text
                                                    as="span"
                                                    fontSize="12px"
                                                    fontWeight="400"
                                                    marginLeft={{ base: '0', xl: '10px' }}
                                                >
                                                    {section.subtitle}
                                                </Text>
                                            )}
                                        </Text>

                                        <AccordionIcon />
                                    </AccordionButton>
                                    <AccordionPanel
                                        padding={{
                                            base: '15px 15px 0',
                                            lg:
                                                index === array.length - 1
                                                    ? '15px 0 0 25px'
                                                    : '15px 0 10px 25px',
                                        }}
                                    >
                                        {section.items.map((item, idx, arr) => (
                                            <Box key={idx}>
                                                <Flex
                                                    paddingBottom={
                                                        index === array.length - 1 &&
                                                        idx === arr.length - 1
                                                            ? '0'
                                                            : '14px'
                                                    }
                                                    alignItems="flex-start"
                                                >
                                                    <Box
                                                        width="20px"
                                                        flexShrink="0"
                                                        marginRight="15px"
                                                        height="auto"
                                                    >
                                                        <Box
                                                            display="flex"
                                                            justifyContent="center"
                                                            height="22px"
                                                            alignItems="center"
                                                        >
                                                            <CheckIcon
                                                                color={'#1AAC7A'}
                                                                fontSize={'15px'}
                                                                //paddingBottom={'1px'}
                                                                //transform={'translateY(-1px)'}
                                                            />
                                                        </Box>
                                                    </Box>
                                                    <Text
                                                        fontSize="15px"
                                                        dangerouslySetInnerHTML={{
                                                            __html: DOMPurify.sanitize(item),
                                                        }}
                                                    />
                                                </Flex>
                                            </Box>
                                        ))}
                                    </AccordionPanel>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </Box>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default ModalSection;
